import { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@components/ui';
import { PlusIcon } from '@heroicons/react/24/outline';
import { AddressForm } from './AddressForm';
import type { Address } from '../types/customer.types';
import { v4 as uuidv4 } from 'uuid';

interface AddressListManagerProps {
  addresses: Address[];
  onChange: (addresses: Address[]) => void;
}

export function AddressListManager({ addresses, onChange }: AddressListManagerProps) {
  const [addressList, setAddressList] = useState<Partial<Address>[]>(() => {
    if (addresses.length > 0) {
      // Ensure existing addresses have IDs
      return addresses.map(addr => ({
        ...addr,
        _id: addr._id || uuidv4()
      }));
    }
    // Create default address with unique ID for new customers
    return [
      {
        _id: uuidv4(),
        type: 'BILLING',
        is_default: true,
        country: '',
        line1: '',
      },
    ];
  });

  // Use ref to always have the latest onChange without triggering re-renders
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const handleAddAddress = () => {
    const newAddress: Partial<Address> = {
      _id: uuidv4(),
      type: 'BILLING',
      is_default: false,
      country: '',
      line1: '',
    };
    const updated = [...addressList, newAddress];
    setAddressList(updated);
    onChangeRef.current(updated as Address[]);
  };

  const handleRemoveAddress = useCallback((index: number) => {
    setAddressList(prevList => {
      if (prevList.length === 1) {
        // Don't allow removing the last address
        return prevList;
      }

      const updated = prevList.filter((_, i) => i !== index);

      // If we removed the default address, make the first one default
      const hasDefault = updated.some(addr => addr.is_default);
      if (!hasDefault && updated.length > 0) {
        updated[0].is_default = true;
      }

      onChangeRef.current(updated as Address[]);
      return updated;
    });
  }, []);

  const handleAddressChange = useCallback((index: number, updatedAddress: Partial<Address>) => {
    setAddressList(prevList => {
      const updated = [...prevList];

      // If this address is being set as default, unset all others
      if (updatedAddress.is_default) {
        updated.forEach((addr, i) => {
          if (i !== index) {
            addr.is_default = false;
          }
        });
      }

      updated[index] = updatedAddress;
      onChangeRef.current(updated as Address[]);
      return updated;
    });
  }, []);

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between sticky top-0 bg-white dark:bg-secondary-800 py-2 z-10 mb-4">
        <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
          Direcciones
        </h3>
        <Button
          variant="outline"
          size="sm"
          leftIcon={<PlusIcon className="w-4 h-4" />}
          onClick={handleAddAddress}
        >
          Agregar Dirección
        </Button>
      </div>

      <div className="space-y-4 pb-4">
        {addressList.map((address, index) => (
          <AddressForm
            key={address._id || index}
            address={address}
            index={index}
            onChange={(updated) => handleAddressChange(index, updated)}
            onRemove={addressList.length > 1 ? () => handleRemoveAddress(index) : undefined}
          />
        ))}
      </div>

      {addressList.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg">
          <p className="text-secondary-600 dark:text-secondary-400 mb-4">
            No hay direcciones agregadas
          </p>
          <Button
            leftIcon={<PlusIcon className="w-4 h-4" />}
            onClick={handleAddAddress}
          >
            Agregar Primera Dirección
          </Button>
        </div>
      )}
    </div>
  );
}
